import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Coffee, Dumbbell, ShieldCheck, Check, Users, Sprout, Download, UserRound, Repeat, BarChart3, MessageCircle, RefreshCw, Trash2, Star, Salad, FlaskConical } from "lucide-react";
import { DietAdmin } from "@/components/admin/DietAdmin";
import { WhatsAppOutbox } from "@/components/admin/WhatsAppOutbox";
import { DietStudio } from "@/components/admin/DietStudio";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/site/PasswordInput";
import { API_BASE } from "@/lib/api";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const LEAD_STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-[#5C665A]/10 text-[#5C665A] border-[#5C665A]/30",
  contacted: "bg-[#89937C]/15 text-[#737D67] border-[#89937C]/40",
  quoted: "bg-[#C57B57]/15 text-[#AD6A48] border-[#C57B57]/40",
  won: "bg-[#2D372B]/10 text-[#2D372B] border-[#2D372B]/40",
  lost: "bg-[#A05252]/10 text-[#A05252] border-[#A05252]/30",
};
const LEAD_TYPE_LABEL = {
  corporate: "corporate wellness", supplier: "coffee supplier", wellness: "wellness quiz", concierge: "coffee concierge",
  journey: "AI journey", journey_request: "journey booking", corporate_retreat: "corporate retreat", workout: "workout plan",
};
const waLink = (l) => {
  const digits = (l.phone || "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const msg = encodeURIComponent(`Hi ${l.name || "there"}, Coach Sanjay here from TheJayProject — thanks for your ${LEAD_TYPE_LABEL[l.type] || ""} enquiry! When's a good time to chat?`);
  return `https://wa.me/${digits}?text=${msg}`;
};

const TOKEN_KEY = "admin_token";

const api = () =>
  axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
  });

function StatusBadge({ status }) {
  const styles = {
    paid: "bg-[#89937C]/15 text-[#5C665A]",
    awaiting_verification: "bg-amber-100 text-amber-800",
    pending: "bg-[#E5E2D9] text-[#5C665A]",
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${styles[status] || styles.pending}`}>
      {status || "pending"}
    </span>
  );
}

function LoginForm({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.access_token);
      onLoggedIn(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[#E5E2D9] rounded-3xl p-8">
        <div className="w-12 h-12 rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
          <ShieldCheck size={22} className="text-[#89937C]" />
        </div>
        <h1 className="font-serif-display text-3xl text-[#2D372B] mb-1">Admin</h1>
        <p className="text-sm text-[#5C665A] mb-6">Private area for Coach Sanjay.</p>
        <div className="space-y-3">
          <Input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="bg-[#F7F5F0] border-[#E5E2D9] h-12"
          />
          <PasswordInput
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-[#F7F5F0] border-[#E5E2D9] h-12"
          />
        </div>
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={loading || !email || !password}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Sign in
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "coffee", label: "Coffee Orders", icon: Coffee },
  { id: "bootcamps", label: "Registrations", icon: Dumbbell },
  { id: "leads", label: "Leads", icon: Users },
  { id: "suppliers", label: "Suppliers", icon: Sprout },
  { id: "members", label: "Members", icon: UserRound },
  { id: "subscriptions", label: "Subs", icon: Repeat },
  { id: "diet", label: "JAY Diets", icon: Salad },
  { id: "studio", label: "Diet Studio", icon: FlaskConical },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "analytics", label: "Traffic", icon: BarChart3 },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("enquiries");
  const [data, setData] = useState({ enquiries: [], coffee: [], bootcamps: [], leads: [], suppliers: [], members: [], subscriptions: [], reviews: [], analytics: null });
  const [loading, setLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  useEffect(() => setSelected(new Set()), [tab]);

  const toggleSel = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const SelBox = ({ id }) => (
    <input
      type="checkbox"
      data-testid="select-record"
      checked={selected.has(id)}
      onChange={() => toggleSel(id)}
      className="h-4 w-4 accent-[#2D372B] shrink-0 cursor-pointer mt-1"
    />
  );

  const deleteSelected = async () => {
    if (!window.confirm(`Delete ${selected.size} record(s)? This cannot be undone.`)) return;
    try {
      const r = await api().post("/admin/records/delete", { collection: tab, ids: [...selected] });
      toast.success(`Deleted ${r.data.deleted} record(s)`);
      setSelected(new Set());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const clearTraffic = async () => {
    if (!window.confirm("Delete ALL traffic analytics data? This cannot be undone.")) return;
    try {
      const r = await api().post("/admin/records/delete", { collection: "traffic", delete_all: true });
      toast.success(`Cleared ${r.data.deleted} traffic events`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear traffic");
    }
  };

  const setLeadStatus = async (lid, status) => {
    const prev = data.leads;
    setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === lid ? { ...l, status } : l)) }));
    try {
      await api().post(`/admin/leads/${lid}/status`, { status });
    } catch {
      setData((d) => ({ ...d, leads: prev }));
      toast.error("Could not update status");
    }
  };

  useEffect(() => {
    api()
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, c, b, l, s, m, sub, rev, an, wa] = await Promise.all([
        api().get("/admin/enquiries"),
        api().get("/admin/coffee-orders"),
        api().get("/admin/registrations"),
        api().get("/admin/leads"),
        api().get("/admin/suppliers"),
        api().get("/admin/members"),
        api().get("/admin/subscriptions"),
        api().get("/admin/coffee-reviews"),
        api().get("/admin/analytics?days=30"),
        api().get("/admin/whatsapp/outbox").catch(() => ({ data: { pending: [] } })),
      ]);
      setData({ enquiries: e.data, coffee: c.data, bootcamps: b.data, leads: l.data, suppliers: s.data, members: m.data, subscriptions: sub.data, reviews: rev.data, analytics: an.data, whatsapp: wa.data.pending || [] });
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markPaid = async (orderType, referenceId) => {
    try {
      await api().post("/admin/mark-paid", { order_type: orderType, reference_id: referenceId });
      toast.success("Marked as paid");
      loadAll();
    } catch {
      toast.error("Could not mark as paid");
    }
  };

  const downloadPricelist = async (s) => {
    try {
      const r = await api().get(`/admin/suppliers/${s.id}/pricelist`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.file_name || "pricelist";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download price list");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking)
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#89937C]" />
      </div>
    );
  if (!user) return <LoginForm onLoggedIn={setUser} />;

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif-display text-4xl text-[#2D372B]">Admin</h1>
            <p className="text-sm text-[#5C665A]">{user.email}</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#E5E2D9] text-[#5C665A] hover:bg-white transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors ${
                tab === t.id ? "bg-[#2D372B] text-white" : "bg-white border border-[#E5E2D9] text-[#5C665A]"
              }`}
            >
              <t.icon size={14} /> {t.label}
              <span className="text-xs opacity-70">({t.id === "analytics" ? (data.analytics?.visitors ?? 0) : (data[t.id] || []).length})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#89937C]" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap pb-1" data-testid="admin-toolbar">
              <div className="text-xs text-[#5C665A]">
                {tab === "analytics" ? "Aggregated traffic data" : tab === "whatsapp" ? "Pending WhatsApp messages" : selected.size > 0 ? `${selected.size} selected` : "Tick records to select them for deletion"}
              </div>
              <div className="flex items-center gap-2">
                {selected.size > 0 && tab !== "analytics" && (
                  <button
                    data-testid="delete-selected-btn"
                    onClick={deleteSelected}
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-[#A05252] text-white font-medium hover:bg-[#8A4444] transition-colors"
                  >
                    <Trash2 size={13} /> Delete selected ({selected.size})
                  </button>
                )}
                {tab === "analytics" && (
                  <button
                    data-testid="clear-traffic-btn"
                    onClick={clearTraffic}
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border border-[#A05252]/40 text-[#A05252] font-medium hover:bg-[#A05252] hover:text-white transition-colors"
                  >
                    <Trash2 size={13} /> Clear all traffic data
                  </button>
                )}
                <button
                  data-testid="admin-refresh-btn"
                  onClick={loadAll}
                  className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border border-[#E5E2D9] bg-white text-[#5C665A] font-medium hover:border-[#89937C] hover:text-[#2D372B] transition-colors"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>
            {tab === "enquiries" &&
              (data.enquiries.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No enquiries yet.</p>
              ) : (
                data.enquiries.map((e, i) => (
                  <div key={e.id || i} data-testid="enquiry-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={e.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="font-medium text-[#2D372B]">{e.name}</div>
                      <div className="text-xs text-[#5C665A]">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                    <a href={`mailto:${e.email}`} className="text-sm text-[#89937C]">{e.email}</a>
                    {e.interest && <div className="text-xs text-[#5C665A] mt-1">Interest: {e.interest}</div>}
                    <p className="text-sm text-[#5C665A] mt-2 leading-relaxed">{e.message}</p>
                    </div>
                  </div>
                ))
              ))}

            {tab === "coffee" &&
              (data.coffee.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No coffee orders yet.</p>
              ) : (
                data.coffee.map((o, i) => (
                  <div key={o.id || i} data-testid="coffee-order-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={o.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[#2D372B] font-medium">
                        {Array.isArray(o.items) && o.items.length > 0
                          ? `Cart order · ${o.items.length} item${o.items.length > 1 ? "s" : ""}${o.name ? ` · ${o.name}` : ""}`
                          : `${{"250":"250 g","500":"500 g","1000":"1 kg","5000":"5 kg","10000":"10 kg","60000":"60 kg Green Bag"}[o.size] || o.size} · ${o.speciality || "—"} · ${o.roast} roast · ${o.grind}`}
                      </div>
                      <StatusBadge status={o.payment_status} />
                    </div>
                    <div className="text-sm text-[#5C665A] mt-1">
                      ₹{o.total || o.price} · {o.payment_method || "razorpay"} · ship: {o.shipping_zone}
                      {o.destination_pincode ? ` (${o.destination_pincode})` : ""}
                      {o.voucher_code ? ` · voucher ${o.voucher_code}` : ""}
                    </div>
                    {Array.isArray(o.items) && o.items.length > 0 && (
                      <div className="text-xs text-[#5C665A] mt-1.5 space-y-0.5" data-testid="cart-order-items">
                        {o.items.map((it, j) => (
                          <div key={j}>
                            • {it.bags}× {({"250":"250 g","500":"500 g","1000":"1 kg","5000":"5 kg","10000":"10 kg"}[it.size] || it.size)} {it.speciality}
                            {it.variant ? ` (${String(it.variant).replace(/_/g, " ")})` : ""} · {it.roast}/{it.grind}
                            {it.blend_arabica_pct != null ? ` · ${it.blend_arabica_pct}% arabica` : ""} · ₹{it.line_total}
                          </div>
                        ))}
                        {o.address && (
                          <div>Ship to: {[o.address.line1, o.address.city, o.address.state, o.address.pincode, o.address.country].filter(Boolean).join(", ")}{o.phone ? ` · ${o.phone}` : ""}</div>
                        )}
                      </div>
                    )}
                    {o.order_note && (
                      <div className="text-xs italic text-[#AD6A48] mt-1" data-testid="cart-order-note">Note: {o.order_note}</div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-[#5C665A]">{new Date(o.created_at).toLocaleString()} · ref {o.id.slice(0, 8)}</div>
                      {o.payment_status !== "paid" && (
                        <button
                          data-testid="mark-paid-btn"
                          onClick={() => markPaid("coffee", o.id)}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#89937C] text-white hover:bg-[#737D67] transition-colors"
                        >
                          <Check size={12} /> Mark as paid
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                ))
              ))}

            {tab === "bootcamps" &&
              (data.bootcamps.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No registrations yet.</p>
              ) : (
                data.bootcamps.map((r, i) => (
                  <div key={r.id || i} data-testid="registration-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={r.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[#2D372B] font-medium">{r.name} — {r.bootcamp_slug}</div>
                      <StatusBadge status={r.payment_status} />
                    </div>
                    <div className="text-sm text-[#5C665A] mt-1">
                      <a href={`mailto:${r.email}`} className="text-[#89937C]">{r.email}</a>
                      {r.phone ? ` · ${r.phone}` : ""} · {r.experience_level}
                      {r.payment_method ? ` · ${r.payment_method}` : ""}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-[#5C665A]">{new Date(r.created_at).toLocaleString()} · ref {r.id.slice(0, 8)}</div>
                      {r.payment_status !== "paid" && (
                        <button
                          data-testid="mark-paid-btn"
                          onClick={() => markPaid("bootcamp", r.id)}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#89937C] text-white hover:bg-[#737D67] transition-colors"
                        >
                          <Check size={12} /> Mark as paid
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                ))
              ))}
            {tab === "leads" && (
              <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="lead-status-filters">
                {["all", ...LEAD_STATUSES].map((s) => {
                  const count = s === "all" ? data.leads.length : data.leads.filter((l) => (l.status || "new") === s).length;
                  return (
                    <button key={s} data-testid={`lead-filter-${s}`} onClick={() => setLeadFilter(s)}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${leadFilter === s ? "bg-[#2D372B] border-[#2D372B] text-[#F7F5F0]" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#89937C]"}`}>
                      {s} ({count})
                    </button>
                  );
                })}
              </div>
            )}
            {tab === "leads" &&
              (data.leads.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No leads yet.</p>
              ) : (
                data.leads.filter((l) => leadFilter === "all" || (l.status || "new") === leadFilter).map((l, i) => (
                  <div key={l.id || i} data-testid="lead-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={l.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[#2D372B] font-medium">
                        {l.type === "corporate"
                          ? `💼 ${l.company} — ${l.employees} employees`
                          : l.type === "supplier"
                          ? `☕ ${l.name} — ${l.origin_country}`
                          : l.type === "wellness"
                          ? `🌿 ${l.name} — ${l.score}/20 (${l.category})`
                          : l.type === "concierge"
                          ? `☕ Concierge — ${l.business} (${l.budget}/mo)`
                          : l.type === "journey"
                          ? `🧭 ${l.name} — ${l.journey_title} (${l.days} days)`
                          : l.type === "journey_request"
                          ? `🔥 BOOKING REQUEST — ${l.name}: ${l.journey_title} (${l.days} days)`
                          : l.type === "corporate_retreat"
                          ? `🏢 RETREAT — ${l.company}: ${l.team_size} people, ${l.duration}`
                          : `🏋️ Workout plan — ${l.goal} · ${l.weight_kg} kg`}
                      </div>
                      <div className="flex items-center gap-2">
                        {waLink(l) && (
                          <a
                            data-testid="lead-whatsapp-btn"
                            href={waLink(l)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1EB855] transition-colors"
                          >
                            <MessageCircle size={12} /> WhatsApp
                          </a>
                        )}
                        <select
                          data-testid="lead-status-select"
                          value={l.status || "new"}
                          onChange={(e) => setLeadStatus(l.id, e.target.value)}
                          className={`text-xs font-semibold uppercase tracking-wider rounded-full border px-3 py-1.5 cursor-pointer focus:outline-none ${STATUS_STYLE[l.status || "new"]}`}
                        >
                          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-[#E5E2D9] text-[#5C665A] font-medium">
                        {l.type}
                      </span>
                    </div>
                    <div className="text-sm text-[#5C665A] mt-1">
                      {l.type === "corporate" ? (
                        <>
                          {l.name} · <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {l.quote ? ` · est ₹${Number(l.quote.monthly_inr).toLocaleString("en-IN")}/mo` : ""}
                        </>
                      ) : l.type === "supplier" ? (
                        <>
                          {l.phone} · <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {` · elevation: ${l.elevation}${l.variety ? ` · variety: ${l.variety}` : ""} · qty: ${l.quantity}`}
                        </>
                      ) : l.type === "wellness" ? (
                        <>
                          <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {l.answers?.goal ? ` · goal: ${l.answers.goal}` : ""}
                        </>
                      ) : l.type === "concierge" ? (
                        <>
                          {l.phone} · <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {` · ${l.drinkers} drinkers · ${l.roast} roast`}
                        </>
                      ) : l.type === "journey_request" ? (
                        <>
                          <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {l.phone && ` · ${l.phone}`}
                          {` · month: ${l.travel_month || "Flexible"} · ${(l.destinations || []).join(" → ")}`}
                          {l.journey_id && <> · <a href={`/journey/${l.journey_id}`} target="_blank" rel="noreferrer" className="text-[#89937C] underline">itinerary</a></>}
                          {l.message && ` · "${l.message}"`}
                        </>
                      ) : l.type === "corporate_retreat" ? (
                        <>
                          {l.name} · <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {l.phone && ` · ${l.phone}`}
                          {` · ${l.month || "Flexible"} · ${l.budget_per_person || "budget open"} · goals: ${(l.goals || []).join(", ") || "—"}`}
                          {l.destination_pref && ` · wants ${l.destination_pref}`}
                        </>
                      ) : l.type === "journey" ? (
                        <>
                          <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {` · from ${l.origin} · ${l.travel_style} · ${l.budget}`}
                          {l.journey_id && <> · <a href={`/journey/${l.journey_id}`} target="_blank" rel="noreferrer" className="text-[#89937C] underline">view itinerary</a></>}
                        </>
                      ) : l.email ? (
                        <>
                          <a href={`mailto:${l.email}`} className="text-[#89937C]">{l.email}</a>
                          {` · equipment: ${(l.equipment || []).join(", ")}`}
                        </>
                      ) : (
                        <span className="italic">Plan generated — email not captured</span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C665A] mt-2">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ))}
            {tab === "suppliers" &&
              (data.suppliers.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No supplier applications yet.</p>
              ) : (
                data.suppliers.map((s) => (
                  <div key={s.id} data-testid="supplier-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={s.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[#2D372B] font-medium">☕ {s.company} — {s.origin_country}{s.region ? ` (${s.region})` : ""}</div>
                      {s.file_name && (
                        <button
                          data-testid="supplier-download-btn"
                          onClick={() => downloadPricelist(s)}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#2D372B] text-white hover:bg-[#89937C] transition-colors"
                        >
                          <Download size={12} /> Price list
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-[#5C665A] mt-1">
                      {s.phone} · <a href={`mailto:${s.email}`} className="text-[#89937C]">{s.email}</a>
                      {s.elevation ? ` · elevation: ${s.elevation}` : ""}
                      {s.variety ? ` · variety: ${s.variety}` : ""}
                      {s.quantity ? ` · qty: ${s.quantity}` : ""}
                    </div>
                    {(s.certifications || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {s.certifications.map((c) => (
                          <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-[#89937C]/15 text-[#5C665A] font-medium">{c}</span>
                        ))}
                      </div>
                    )}
                    <div className="text-xs text-[#5C665A] mt-2">{new Date(s.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ))}
            {tab === "members" && data.members.length > 0 && (
              <div className="flex justify-end mb-1">
                <button
                  data-testid="members-export-csv-btn"
                  onClick={async () => {
                    try {
                      const r = await api().get("/admin/members/export-csv", { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url; a.download = `members_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Members CSV downloaded");
                    } catch { toast.error("Could not export members"); }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium bg-[#2D372B] text-white hover:bg-[#89937C]"
                >
                  <Download size={12} /> Export CSV ({data.members.length})
                </button>
              </div>
            )}
            {tab === "members" &&
              (data.members.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No members yet.</p>
              ) : (
                data.members.map((m) => (
                  <div key={m.user_id} data-testid="member-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={m.user_id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        {m.picture ? <img src={m.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="w-8 h-8 rounded-full bg-[#89937C]/15" />}
                        <div>
                          <div className="text-[#2D372B] font-medium">{m.name || m.email}</div>
                          <div className="text-xs text-[#5C665A]"><a href={`mailto:${m.email}`} className="text-[#89937C]">{m.email}</a>{m.phone ? ` · ${m.phone}` : ""} · via {m.auth_provider}</div>
                        </div>
                      </div>
                      <div className="text-xs text-[#5C665A] text-right">
                        ☕ {m.coffee_orders} orders (₹{Number(m.coffee_spend).toLocaleString("en-IN")}) · 🏋️ {m.bootcamps} bootcamps
                        <div className="mt-0.5">Joined {new Date(m.created_at).toLocaleDateString()}</div>
                        <div className="mt-1.5 flex justify-end gap-1.5">
                          <button
                            data-testid="member-reset-password-btn"
                            onClick={async () => {
                              if (!window.confirm(`Generate a new password for ${m.email}? Their old password will stop working.`)) return;
                              try {
                                const r = await api().post(`/admin/members/${m.user_id}/reset-password`);
                                try { await navigator.clipboard.writeText(r.data.password); } catch { /* clipboard unavailable */ }
                                toast.success(`New password for ${r.data.email}: ${r.data.password} (copied to clipboard — share it with them)`, { duration: 20000 });
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not reset password");
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium border border-[#C57B57]/50 text-[#AD6A48] hover:bg-[#C57B57]/10"
                          >
                            <RefreshCw size={10} /> Reset password
                          </button>
                          <button
                            data-testid="member-delete-btn"
                            onClick={async () => {
                              if (!window.confirm(`DELETE member ${m.email}?\n\nThis removes their login account permanently (orders and records stay). This cannot be undone.`)) return;
                              try {
                                await api().delete(`/admin/members/${m.user_id}`);
                                toast.success(`Deleted ${m.email}`);
                                loadAll();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not delete member");
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium border border-[#A05252]/50 text-[#A05252] hover:bg-[#A05252]/10"
                          >
                            <Trash2 size={10} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                    {(m.addresses || []).length > 0 && (
                      <div className="text-xs text-[#5C665A] mt-2">
                        📍 {m.addresses.map((a) => `${a.label}: ${[a.city, a.pincode].filter(Boolean).join(" ")}${a.is_default ? " (default)" : ""}`).join(" · ")}
                      </div>
                    )}
                    </div>
                  </div>
                ))
              ))}
            {tab === "subscriptions" &&
              (data.subscriptions.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No subscriptions yet.</p>
              ) : (
                data.subscriptions.map((s) => (
                  <div key={s.id} data-testid="admin-sub-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={s.id} />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[#2D372B] font-medium capitalize">
                        🔁 {(s.speciality || "").replace("_", " ")} · {s.roast} · {Number(s.size) >= 1000 ? `${s.size / 1000} kg` : `${s.size} g`} · {s.frequency}
                      </div>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${s.status === "active" ? "bg-[#89937C]/15 text-[#2D372B]" : "bg-[#AD6A48]/15 text-[#8A5236]"}`}>{s.status}</span>
                    </div>
                    <div className="text-sm text-[#5C665A] mt-1">
                      {s.member_name || "Member"} · <a href={`mailto:${s.email}`} className="text-[#89937C]">{s.email}</a> · next: {s.next_delivery}
                      {s.address ? ` · ships to ${[s.address.city, s.address.pincode].filter(Boolean).join(" ")}` : " · no address on file"}
                    </div>
                    </div>
                  </div>
                ))
              ))}
            {tab === "diet" && <DietAdmin api={api} />}
            {tab === "studio" && <DietStudio api={api} />}
            {tab === "whatsapp" && <WhatsAppOutbox api={api} />}

            {tab === "reviews" &&
              (data.reviews.length === 0 ? (
                <p className="text-sm text-[#5C665A] py-10 text-center">No reviews yet.</p>
              ) : (
                data.reviews.map((r) => (
                  <div key={r.id} data-testid="review-row" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start gap-3">
                    <SelBox id={r.id} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-[#2D372B] font-medium">
                          {r.name} · {String(r.speciality).replace(/_/g, " ")} — {String(r.variant).replace(/_/g, " ")}
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} size={14} className={i <= r.rating ? "text-[#C57B57] fill-[#C57B57]" : "text-[#E5E2D9]"} />
                          ))}
                        </div>
                      </div>
                      {r.comment && <div className="text-sm text-[#5C665A] mt-1">{r.comment}</div>}
                      <div className="text-xs text-[#5C665A] mt-2">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ))}

            {tab === "analytics" && data.analytics && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[["Visitors (30d)", data.analytics.visitors], ["Page views", data.analytics.pageviews], ["Pages / visit", data.analytics.avg_session_pages], ["Countries", Object.keys(data.analytics.countries || {}).length]].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#E5E2D9] rounded-2xl p-4 text-center">
                      <div className="font-serif-display text-3xl text-[#2D372B]">{v}</div>
                      <div className="text-xs text-[#5C665A] mt-1">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid="traffic-trend-chart">
                  <div className="text-sm font-medium text-[#2D372B] mb-3">Daily visitors &amp; page views</div>
                  {(!data.analytics.daily || data.analytics.daily.length === 0) ? (
                    <p className="text-xs text-[#5C665A]">No data yet.</p>
                  ) : (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.analytics.daily} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#C57B57" stopOpacity={0.35} />
                              <stop offset="95%" stopColor="#C57B57" stopOpacity={0.03} />
                            </linearGradient>
                            <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#89937C" stopOpacity={0.45} />
                              <stop offset="95%" stopColor="#89937C" stopOpacity={0.05} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5C665A" }} tickFormatter={(d) => d.slice(5)} />
                          <YAxis tick={{ fontSize: 10, fill: "#5C665A" }} allowDecimals={false} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2D9", fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Area type="monotone" dataKey="pageviews" name="Page views" stroke="#C57B57" strokeWidth={2} fill="url(#gPv)" />
                          <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#89937C" strokeWidth={2} fill="url(#gVis)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid="traffic-pages-chart">
                  <div className="text-sm font-medium text-[#2D372B] mb-3">Top pages by views</div>
                  {(data.analytics.pages || []).length === 0 ? (
                    <p className="text-xs text-[#5C665A]">No data yet.</p>
                  ) : (
                    <div style={{ height: Math.max(160, Math.min((data.analytics.pages || []).length, 8) * 34) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={(data.analytics.pages || []).slice(0, 8)} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                          <XAxis type="number" tick={{ fontSize: 10, fill: "#5C665A" }} allowDecimals={false} />
                          <YAxis type="category" dataKey="path" width={140} tick={{ fontSize: 10, fill: "#2D372B" }} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2D9", fontSize: 12 }} />
                          <Bar dataKey="views" name="Views" fill="#89937C" radius={[0, 6, 6, 0]} barSize={16} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
                  <div className="text-sm font-medium text-[#2D372B] mb-3">Business line interest</div>
                  {Object.keys(data.analytics.business_lines || {}).length === 0 ? <p className="text-xs text-[#5C665A]">No data yet.</p> : (
                    <div className="space-y-2">
                      {Object.entries(data.analytics.business_lines).map(([k, v]) => {
                        const max = Math.max(...Object.values(data.analytics.business_lines));
                        return (
                          <div key={k} className="flex items-center gap-3">
                            <span className="text-xs text-[#5C665A] w-36 shrink-0">{k}</span>
                            <div className="flex-1 h-3 bg-[#F7F5F0] rounded-full overflow-hidden"><div className="h-full bg-[#89937C]" style={{ width: `${(v / max) * 100}%` }} /></div>
                            <span className="text-xs text-[#2D372B] font-medium w-10 text-right">{v}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
                  <div className="text-sm font-medium text-[#2D372B] mb-3">Pages — views · avg time · exits</div>
                  <div className="space-y-1.5">
                    {(data.analytics.pages || []).map((p) => (
                      <div key={p.path} className="flex justify-between text-xs text-[#5C665A]">
                        <span className="text-[#2D372B]">{p.path}</span>
                        <span>{p.views} views · {p.avg_time_sec}s · {p.exits} exits</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[["Countries", data.analytics.countries], ["Cities", data.analytics.cities], ["Devices", data.analytics.devices]].map(([label, obj]) => (
                    <div key={label} className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
                      <div className="text-sm font-medium text-[#2D372B] mb-2">{label}</div>
                      {Object.keys(obj || {}).length === 0 ? <p className="text-xs text-[#5C665A]">No data yet.</p> :
                        Object.entries(obj).map(([k, v]) => (
                          <div key={k} className="flex justify-between text-xs text-[#5C665A]"><span>{k}</span><span className="text-[#2D372B] font-medium">{v}</span></div>
                        ))}
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
                  <div className="text-sm font-medium text-[#2D372B] mb-2">Home page sections viewed</div>
                  {Object.entries(data.analytics.sections || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs text-[#5C665A]"><span className="capitalize">{k}</span><span className="text-[#2D372B] font-medium">{v}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
